// Dynamic mammoth styleMap entries for custom (client-specific) heading
// paragraph style NAMES — e.g. "CAM Heading 1" — that mammoth's built-in
// styleMap can't know about ahead of time. Such paragraphs flatten to plain
// text ("Unrecognised paragraph style") because a static styleMap can't
// enumerate arbitrary style names. Word marks a paragraph style as a heading
// via `w:outlineLvl`, independent of its name, so any style carrying one gets
// an entry mapping its friendly `w:name` to the matching h1-h6.
import { readDocxXml } from './ooxml/readDocxXml';
import { parseStyles, resolveHeadingStyleNames } from './ooxml/styles';
import type { HeadingStyleName } from './ooxml/styles';
import { DOCX_IMPORT_STYLE_MAP } from './docxImportConstants';

// Defensive cap — a hostile or degenerate styles.xml must not blow up
// mammoth's styleMap with an unbounded number of rules.
const MAX_DYNAMIC_HEADING_ENTRIES = 30;

// mammoth's own default style-name heading matchers (see mammoth's
// options-reader.js `_defaultStyleMap`) — a doc style genuinely named one of
// these already gets mapped by mammoth itself; skip it here.
const MAMMOTH_BUILTIN_HEADING_NAMES = ['Heading 1', 'Heading 2', 'Heading 3', 'Heading 4', 'Heading 5', 'Heading 6', 'Heading'];

const STATIC_STYLE_NAME_RE = /style-name='([^']+)'/;

// Both inputs are static, so this is computed once at module load rather than
// per import.
const RESERVED_STYLE_NAMES: Set<string> = (() => {
    const reserved = new Set(MAMMOTH_BUILTIN_HEADING_NAMES.map((name) => name.toUpperCase()));
    for (const rule of DOCX_IMPORT_STYLE_MAP) {
        const name = STATIC_STYLE_NAME_RE.exec(rule)?.[1];
        if (name) reserved.add(name.toUpperCase());
    }
    return reserved;
})();

// mammoth's style-name DSL wraps the name in single quotes; escape a literal
// backslash/quote so a (rare) style name can't corrupt the rule.
function escapeStyleName(name: string): string {
    return name.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

/**
 * Filters out headings whose name collides with a reserved (mammoth built-in
 * or already-static) style name, de-dupes by name, caps the result, and
 * formats each as a mammoth styleMap rule. Pure — no XML/zip involved.
 */
export function formatDynamicHeadingStyleMap(headings: HeadingStyleName[]): string[] {
    const seen = new Set<string>();
    const entries: string[] = [];

    for (const heading of headings) {
        if (entries.length >= MAX_DYNAMIC_HEADING_ENTRIES) break;
        const name = heading.name.trim();
        const key = name.toUpperCase();
        if (!name || RESERVED_STYLE_NAMES.has(key) || seen.has(key)) continue;
        seen.add(key);
        entries.push(`p[style-name='${escapeStyleName(name)}'] => h${heading.level + 1}:fresh`);
    }

    return entries;
}

/**
 * Best-effort: reads word/styles.xml out of the .docx and returns mammoth
 * styleMap entries for its custom heading styles. Returns [] for a missing or
 * unparseable styles.xml, or on any read/parse failure — mirrors
 * applyOoxmlFidelity's additive, never-worse-than-mammoth-alone contract.
 */
export async function buildDynamicHeadingStyleMap(arrayBuffer: ArrayBuffer): Promise<string[]> {
    try {
        const { styles } = await readDocxXml(arrayBuffer);
        return formatDynamicHeadingStyleMap(resolveHeadingStyleNames(parseStyles(styles)));
    } catch {
        return [];
    }
}
